import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DESTRUCTIVE_OUTLINE } from '../admin/styles';

interface ConfirmDeleteDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    confirmLabel: string;
    /** Runs the deletion; the dialog stays open, its buttons disabled, until it settles. */
    onConfirm: () => Promise<void>;
}

/**
 * "Are you sure?" before something is deleted for good. The confirm button is
 * outlined red, never filled; it waits for the deletion before closing so a
 * failure leaves the person where they were.
 */
export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({ open, onOpenChange, title, description, confirmLabel, onConfirm }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    return (
        <AlertDialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>{description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy}>{t('COMMON.CANCEL', 'Cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                        variant="outline"
                        className={DESTRUCTIVE_OUTLINE}
                        disabled={busy}
                        onClick={async (e) => {
                            e.preventDefault();
                            setBusy(true);
                            try { await onConfirm(); onOpenChange(false); } catch { /* stays open */ } finally { setBusy(false); }
                        }}
                    >
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
